/** Company Analytics — v1's analyticsPanel layout, section for section:
 * dark hero + range picker; 8 KPI tiles; Lead Flow / SMS Outreach / Closing
 * Risk; Revenue vs Expenses + Lead Sources donut; Funnel + Deals by Stage;
 * Top Markets + Expense Breakdown; ROI Distribution + Team Performance;
 * Business Mix; the modeled-estimates disclaimer. All client-side. */
import { useMemo, useState } from "react";
import type { Lead } from "../../types";
import type { Deal } from "../deals/api";
import type { WholesaleDeal } from "../wholesale/api";
import type { EditsMap, TaskRec } from "../edits/api";
import type { Thread } from "../inbox/api";
import type { Member } from "../team/api";
import { mergeMaster } from "../roi/math";
import {
  ANL_MSG, ANL_RANGES, AREA_H, AREA_W, analyticsAgg, areaPoints, areaX, buildAnlLeads,
  closingRisk, donutSegs, leadFlow, outreach, pipeColor, rateTone, riskPill, usd,
  type AnlRange,
} from "./api";

export function AnalyticsPage({
  leads,
  edits,
  deals,
  tasks,
  wholesale,
  members,
  orgSettings,
  threads,
  onOpenDeals,
}: {
  leads: Lead[];
  edits: EditsMap;
  deals: Deal[];
  tasks: TaskRec[];
  wholesale: WholesaleDeal[];
  members: Member[];
  orgSettings: Record<string, unknown>;
  threads: Thread[] | null;
  onOpenDeals: () => void;
}) {
  const [range, setRange] = useState<AnlRange>("ytd"); // v1 default: 6 Months
  const now = useMemo(() => new Date(), []);

  const props = useMemo(() => buildAnlLeads(leads, edits), [leads, edits]);
  const master = useMemo(() => mergeMaster(orgSettings["NR_MASTER"]), [orgSettings]);
  const memberNames = useMemo(
    () => members.map((m) => m.name ?? (m.email.split("@")[0] ?? "").replace(/^./, (c) => c.toUpperCase())),
    [members],
  );
  const a = useMemo(
    () => analyticsAgg(props, deals, wholesale, tasks, memberNames, [], range, master, now),
    [props, deals, wholesale, tasks, memberNames, range, master, now],
  );
  const flow = useMemo(() => leadFlow(props, now), [props, now]);
  const risk = useMemo(() => closingRisk(deals, tasks, now), [deals, tasks, now]);
  const sms = useMemo(() => {
    if (threads === null) return null;
    const all = threads.flatMap((t) => t.messages);
    return outreach(all, now);
  }, [threads, now]);

  const rev = areaPoints(a.months, "rev");
  const exp = areaPoints(a.months, "exp");
  const donut = donutSegs(a.sources);
  const maxStage = Math.max(1, ...a.stageRows.map((r) => r[1]));
  const maxCty = Math.max(1, ...a.counties.map((c) => c.profit));
  const maxBucket = Math.max(1, ...a.buckets.map((b) => b[1]));
  const funnelBase = Math.max(a.funnel[0]?.[1] ?? 1, 1);
  const mixTotal = Math.max(a.flipN + a.whN, 1);

  const kpis: [string, string, string][] = [
    ["Pipeline Value", usd(a.pipelineVal), `${a.totalDeals.toLocaleString("en-US")} total deals`],
    ["Projected Profit", usd(a.totalProfit), "across active + closed"],
    ["Avg ROI", a.avgRoi.toFixed(1) + "%", "per flip"],
    ["Avg Days to Sell", a.avgDays ? a.avgDays + "d" : "—", "market cycle time"],
    ["Active Deals", String(a.active), `${a.sold} sold`],
    ["Avg Profit / Flip", usd(a.avgProfit), "gross"],
    ["Capital Deployed", usd(a.capital), "cash to close"],
    ["Wholesale Fees", usd(a.whFees), `${a.whN} assignments`],
  ];

  // Purely decorative — emoji + color hue per KPI tile (v1 _anlKpi icon chips),
  // index-aligned with `kpis` above. No effect on any metric or logic.
  const kpiIcons: [string, string][] = [
    ["💰", "blue"],    // Pipeline Value  (v1: layers / blue)
    ["📈", "emerald"], // Projected Profit (v1: trending-up / emerald)
    ["💹", "violet"],  // Avg ROI          (v1: percent / violet)
    ["⏱️", "amber"],   // Avg Days to Sell (v1: clock / amber)
    ["💼", "sky"],     // Active Deals     (v1: briefcase / sky)
    ["💵", "emerald"], // Avg Profit/Flip  (v1: dollar-sign / emerald)
    ["💸", "rose"],    // Capital Deployed (v1: wallet / rose)
    ["🤝", "orange"],  // Wholesale Fees   (v1: handshake / orange)
  ];

  return (
    <div className="anl">
      <div className="anlhero">
        <div className="anlherobrand">
          <span className="anlherobadge" aria-hidden="true">📊</span>
          <div className="anlherotext">
            <h1>{ANL_MSG.title}</h1>
            <p>{ANL_MSG.sub}</p>
          </div>
        </div>
        <div className="anlranges" role="tablist">
          {ANL_RANGES.map(([k, label]) => (
            <button key={k} className={"anlrange" + (range === k ? " on" : "")} onClick={() => setRange(k)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="anlkpis">
        {kpis.map(([label, val, sub], i) => {
          const [icon, hue] = kpiIcons[i] ?? ["•", "blue"];
          return (
          <div key={label} className="anlkpi">
            <div className="anlkpitop">
              <div className="lbl">{label}</div>
              <span className={"anlkpiico ico-" + hue} aria-hidden="true">{icon}</span>
            </div>
            <div className="anlkpival">{val}</div>
            <div className="anlkpisub">{sub}</div>
          </div>
          );
        })}
      </div>

      <div className="anlrow three">
        <section className="anlcard">
          <h3>Lead Flow</h3>
          <div className="anlminis">
            {([["Today", flow.today], ["Yesterday", flow.yesterday], ["Last 7 days", flow.week]] as [string, number][]).map(([l, v]) => (
              <div key={l} className="anlmini">
                <b>{v.toLocaleString("en-US")}</b>
                <span>{l}</span>
              </div>
            ))}
          </div>
          <div className="anlbars" aria-label="Leads per day — last 14 days">
            {flow.days.map((d) => (
              <div key={d.t} className="anlbar" title={`${d.n} leads`} style={{ height: Math.max((d.n / flow.maxD) * 100, 4) + "%" }} />
            ))}
          </div>
          <div className="anlcaption">Leads per day — last 14 days</div>
          <div className="anlkv">
            <span>Qualified</span>
            <b>
              {flow.qualified}/{flow.total.toLocaleString("en-US")}
            </b>
          </div>
          <div className="anlkv">
            <span>Price drops (motivated)</span>
            <b className="anlred">{flow.drops}</b>
          </div>
        </section>

        <section className="anlcard">
          <h3>SMS Outreach</h3>
          {sms === null ? (
            <div className="anlempty">{ANL_MSG.noSms}</div>
          ) : (
            <>
              <div className="anlminis two">
                <div className="anlmini"><b className="anlblue">{sms.sent}</b><span>Sent</span></div>
                <div className="anlmini"><b className="anlgreen">{sms.received}</b><span>Received</span></div>
                <div className="anlmini"><b>{sms.contacts}</b><span>Contacts texted</span></div>
                <div className="anlmini"><b>{sms.activeWeek}</b><span>Active this week</span></div>
              </div>
              <div className="anlkv">
                <span>Response rate</span>
                <b className={"rate-" + rateTone(sms.rate)}>{sms.rate}%</b>
              </div>
              <div className="anlmeter">
                <div className={"anlmeterfill rate-" + rateTone(sms.rate)} style={{ width: Math.max(sms.rate, 2) + "%" }} />
              </div>
              <div className="anlcaption">{ANL_MSG.outreachCaption(sms.replied, sms.contacts)}</div>
            </>
          )}
        </section>

        <section className="anlcard">
          <h3>Closing Risk — Pending Deals</h3>
          {risk.length ? (
            <div className="anlrisks">
              {risk.map((r) => (
                <button key={r.address} className={"anlrisk " + r.tone} onClick={onOpenDeals}>
                  <span className="anlriskrow">
                    <span className="anlriskaddr">{(r.address.split(",")[0] ?? "").trim()}</span>
                    <span className={"anlriskpill " + r.tone}>{riskPill(r.dtc)}</span>
                  </span>
                  <span className="anlrisksub">
                    {r.open} open task{r.open !== 1 ? "s" : ""}
                    {r.close ? " · closes " + r.close : ""}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="anlempty">{ANL_MSG.noPending}</div>
          )}
        </section>
      </div>

      <div className="anlrow twoone">
        <section className="anlcard wide">
          <div className="anlcardhead">
            <h3>Revenue vs Expenses</h3>
            <span className="anllegend">
              <span><i className="dot rev" />Revenue</span>
              <span><i className="dot exp" />Expenses</span>
            </span>
          </div>
          <svg viewBox={`0 0 ${AREA_W} ${AREA_H}`} className="anlarea" role="img" aria-label="Revenue vs Expenses">
            {[0.25, 0.5, 0.75, 1].map((f) => (
              <line key={f} x1={24} x2={AREA_W - 24} y1={AREA_H - 24 - f * (AREA_H - 48)} y2={AREA_H - 24 - f * (AREA_H - 48)} className="anlgrid" />
            ))}
            <path d={rev.area} fill="#2563eb" opacity="0.08" />
            <polyline points={rev.pts} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d={exp.area} fill="#f43f5e" opacity="0.08" />
            <polyline points={exp.pts} fill="none" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {a.months.map((m, i) => (
              <g key={m.key}>
                <circle cx={areaX(i, a.months.length)} cy={0} r={0} fill="none" />
                <text x={areaX(i, a.months.length)} y={AREA_H - 6} className="anlaxis" textAnchor="middle">
                  {m.label}
                </text>
              </g>
            ))}
          </svg>
        </section>

        <section className="anlcard">
          <h3>Lead Sources</h3>
          <div className="anldonutrow">
            <svg viewBox="0 0 140 140" className="anldonut" role="img" aria-label="Lead sources">
              {donut.segs.map((s, i) => (
                <circle
                  key={i}
                  cx="70" cy="70" r="54" fill="none"
                  stroke={s.color} strokeWidth="20"
                  strokeDasharray={`${s.len} ${donut.c - s.len}`}
                  strokeDashoffset={-s.off}
                  transform="rotate(-90 70 70)"
                />
              ))}
              <circle cx="70" cy="70" r="40" className="anldonuthole" />
              <text x="70" y="66" className="anldonutlbl" textAnchor="middle">Sources</text>
              <text x="70" y="82" className="anldonutnum" textAnchor="middle">
                {a.sources.reduce((s, x) => s + x.count, 0).toLocaleString("en-US")}
              </text>
            </svg>
            <div className="anlsrclist">
              {a.sources.slice(0, 6).map((s) => (
                <div key={s.name} className="anlsrc">
                  <span className="anlsrcname">
                    <i className="dot" style={{ background: s.color }} />
                    {s.name}
                  </span>
                  <span className="anlsrcpct">{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <div className="anlrow two">
        <section className="anlcard">
          <h3>Deal Pipeline Funnel</h3>
          <div className="anlfunnel">
            {a.funnel.map((f, i) => {
              const pct = Math.round((f[1] / funnelBase) * 100);
              const conv = i > 0 ? Math.round((f[1] / Math.max(a.funnel[i - 1]?.[1] ?? 1, 1)) * 100) : 100;
              return (
                <div key={f[0]}>
                  <div className="anlkv tight">
                    <span className="anlfname">{f[0]}</span>
                    <span className="anlfnum">
                      {f[1].toLocaleString("en-US")}
                      {i > 0 ? ` · ${conv}% conv` : ""}
                    </span>
                  </div>
                  <div className="anlfbarwrap">
                    <div className="anlfbar" style={{ width: Math.max(pct, 3) + "%" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="anlcard">
          <h3>Deals by Stage</h3>
          <div className="anlstages">
            {a.stageRows.map(([l, n]) => (
              <div key={l} className="anlstage">
                <span className="anlstagename">{l}</span>
                <div className="anlstagebarwrap">
                  <div className="anlstagebar" style={{ width: Math.max((n / maxStage) * 100, 8) + "%", background: pipeColor(l) }}>
                    {n || ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="anlrow two">
        <section className="anlcard">
          <h3>Top Markets by Profit</h3>
          {a.counties.length ? (
            <div className="anlmarkets">
              {a.counties.map((c) => (
                <div key={c.name}>
                  <div className="anlkv tight">
                    <span className="anlfname">{c.name} County</span>
                    <span className="anlgreen b">{usd(c.profit)}</span>
                  </div>
                  <div className="anlfbarwrap slim">
                    <div className="anlfbar green" style={{ width: Math.max((c.profit / maxCty) * 100, 4) + "%" }} />
                  </div>
                  <div className="anlcaption">{c.count} deal{c.count !== 1 ? "s" : ""}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="anlempty">{ANL_MSG.noMarket}</div>
          )}
        </section>

        <section className="anlcard">
          <h3>Expense Breakdown</h3>
          {a.expCats.length ? (
            <div className="anlmarkets">
              {a.expCats.slice(0, 8).map((x) => (
                <div key={x.cat}>
                  <div className="anlkv tight">
                    <span className="anlfname">
                      <i className="dot" style={{ background: x.color }} />
                      {x.cat}
                    </span>
                    <span>{usd(x.total)} · {x.pct}%</span>
                  </div>
                  <div className="anlfbarwrap slim">
                    <div className="anlfbar" style={{ width: Math.max((x.total / Math.max(...a.expCats.map((e) => e.total), 1)) * 100, 3) + "%", background: x.color }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="anlempty">{ANL_MSG.noExp}</div>
          )}
        </section>
      </div>

      <div className="anlrow two">
        <section className="anlcard">
          <h3>ROI Distribution</h3>
          <div className="anlbuckets">
            {a.buckets.map((b) => (
              <div key={b[0]} className="anlbucket">
                <div className="anlbucketn">{b[1]}</div>
                <div className="anlbucketbar" style={{ height: Math.max((b[1] / maxBucket) * 120, 4) + "px", background: b[2] }} />
                <div className="anlbucketlbl">{b[0]}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="anlcard">
          <h3>Team Performance</h3>
          <div className="anltablewrap">
            <table className="anltable">
              <thead>
                <tr>
                  <th>Member</th>
                  <th className="r">Deals</th>
                  <th className="r">Tasks Done</th>
                  <th className="r">Open</th>
                  <th className="r">Profit</th>
                </tr>
              </thead>
              <tbody>
                {a.team.map((t) => (
                  <tr key={t.name}>
                    <td className="b">{t.name}</td>
                    <td className="r">{t.deals.toLocaleString("en-US")}</td>
                    <td className="r anlgreen">{t.tasksDone}</td>
                    <td className="r anlamber">{t.tasksOpen}</td>
                    <td className="r b">{usd(t.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="anlcard">
        <div className="anlcardhead">
          <div>
            <h3>Business Mix</h3>
            <div className="anlcaption">Fix &amp; Flip vs Wholesale</div>
          </div>
        </div>
        <div className="anlmix">
          <div className="anlmixflip" style={{ width: (a.flipN / mixTotal) * 100 + "%" }}>
            Flip {a.flipN.toLocaleString("en-US")}
          </div>
          <div className="anlmixwh" style={{ width: (a.whN / mixTotal) * 100 + "%" }}>
            Wholesale {a.whN}
          </div>
        </div>
      </section>

      <p className="anldisclaimer">{ANL_MSG.disclaimer}</p>
    </div>
  );
}
